const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class DarkrimProdNav extends HTMLElement {
  #activeIndex = -1;
  #frame = null;
  #links = [];
  #pinnedIndex = -1;
  #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  #scrollRoot = null;

  connectedCallback() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const isDesignMode = document.documentElement.classList.contains('shopify-design-mode');
    if (isMobile && !isDesignMode) return;

    this.#links = Array.from(this.querySelectorAll('[data-darkrim-prod-nav-link]'));
    if (this.#links.length === 0) return;

    this.#scrollRoot = this.#getScrollRoot();
    this.addEventListener('click', this.#handleClick);
    this.#scrollRoot.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#scrollRoot.addEventListener('wheel', this.#unpin, { passive: true });
    this.#scrollRoot.addEventListener('touchstart', this.#unpin, { passive: true });
    this.#scrollRoot.addEventListener('keydown', this.#handleScrollKey);
    window.addEventListener('resize', this.#handleScroll, { passive: true });
    document.addEventListener('shopify:section:load', this.#handleStructureChange);
    document.addEventListener('shopify:section:unload', this.#handleStructureChange);
    document.addEventListener('shopify:section:reorder', this.#handleStructureChange);

    const hashId = this.#normalizeId(window.location.hash.replace('#', ''));
    if (hashId && this.#targetForId(hashId)) {
      this.#scrollToId(hashId, true);
    } else {
      this.#updateActive();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#scrollRoot?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollRoot?.removeEventListener('wheel', this.#unpin);
    this.#scrollRoot?.removeEventListener('touchstart', this.#unpin);
    this.#scrollRoot?.removeEventListener('keydown', this.#handleScrollKey);
    window.removeEventListener('resize', this.#handleScroll);
    document.removeEventListener('shopify:section:load', this.#handleStructureChange);
    document.removeEventListener('shopify:section:unload', this.#handleStructureChange);
    document.removeEventListener('shopify:section:reorder', this.#handleStructureChange);

    if (this.#frame !== null) window.cancelAnimationFrame(this.#frame);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('[data-darkrim-prod-nav-link]');
    if (!link || !this.contains(link)) return;

    const targetId = this.#normalizeId(link.getAttribute('href')?.replace('#', ''));
    if (!targetId || !this.#targetForId(targetId)) return;

    event.preventDefault();
    this.#pinnedIndex = this.#links.indexOf(link);
    this.#setActive(this.#pinnedIndex);
    this.#scrollToId(targetId, this.#reducedMotionQuery.matches);
  };

  #handleScroll = () => {
    if (this.#pinnedIndex >= 0) return;
    this.#scheduleActiveUpdate();
  };

  #unpin = () => {
    if (this.#pinnedIndex < 0) return;
    this.#pinnedIndex = -1;
  };

  #handleScrollKey = (event) => {
    const keys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
    if (!keys.includes(event.key)) return;
    this.#unpin();
  };

  #handleStructureChange = () => {
    this.#scrollRoot?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollRoot?.removeEventListener('wheel', this.#unpin);
    this.#scrollRoot?.removeEventListener('touchstart', this.#unpin);
    this.#scrollRoot?.removeEventListener('keydown', this.#handleScrollKey);
    this.#scrollRoot = this.#getScrollRoot();
    this.#scrollRoot.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#scrollRoot.addEventListener('wheel', this.#unpin, { passive: true });
    this.#scrollRoot.addEventListener('touchstart', this.#unpin, { passive: true });
    this.#scrollRoot.addEventListener('keydown', this.#handleScrollKey);
    this.#scheduleActiveUpdate();
  };

  #scheduleActiveUpdate() {
    if (this.#pinnedIndex >= 0 || this.#frame !== null) return;

    this.#frame = window.requestAnimationFrame(() => {
      this.#frame = null;
      if (this.#pinnedIndex < 0) this.#updateActive();
    });
  }

  #updateActive() {
    if (this.#pinnedIndex >= 0) {
      this.#setActive(this.#pinnedIndex);
      return;
    }

    const offset = this.#getOffset();
    let nextIndex = -1;
    let closestTop = Number.NEGATIVE_INFINITY;

    this.#links.forEach((link, index) => {
      const target = this.#targetFor(link);
      if (!target) return;

      const top = target.getBoundingClientRect().top;
      if (top <= offset + 1 && top >= closestTop) {
        closestTop = top;
        nextIndex = index;
      }
    });

    if (nextIndex < 0) nextIndex = this.#fallbackIndex();
    this.#setActive(nextIndex);
  }

  #fallbackIndex() {
    let firstIndex = 0;
    let firstTop = Number.POSITIVE_INFINITY;

    this.#links.forEach((link, index) => {
      const target = this.#targetFor(link);
      if (!target) return;

      const top = target.getBoundingClientRect().top;
      if (top < firstTop) {
        firstTop = top;
        firstIndex = index;
      }
    });

    return firstIndex;
  }

  #setActive(index) {
    if (index < 0 || index === this.#activeIndex) return;

    this.#activeIndex = index;
    this.#links.forEach((link, linkIndex) => {
      const isActive = linkIndex === index;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  #scrollToId(id, immediate) {
    const target = this.#targetForId(id);
    if (!target) return;

    const root = this.#scrollRoot || this.#getScrollRoot();
    const current = this.#isDocumentRoot(root) ? window.scrollY : root.scrollTop;
    const next = current + target.getBoundingClientRect().top - this.#getOffset();

    root.scrollTo({
      top: Math.max(0, next),
      behavior: immediate ? 'auto' : 'smooth',
    });

    if (this.#normalizeId(window.location.hash.replace('#', '')) !== id) {
      history.replaceState(null, '', `#${id}`);
    }

    const linkIndex = this.#links.findIndex(
      (link) => this.#normalizeId(link.getAttribute('href')?.replace('#', '')) === id,
    );
    if (linkIndex >= 0) {
      this.#pinnedIndex = linkIndex;
      this.#setActive(linkIndex);
    }
  }

  #getOffset() {
    if (!this.classList.contains('darkrim-prod-nav--sticky')) return 0;

    const headerIsSticky = Boolean(
      document.querySelector(
        '#header-component[sticky="always"], #header-component[sticky="scroll-up"][data-sticky-state="active"]',
      ),
    );
    const headerHeight = headerIsSticky
      ? Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0
      : 0;

    return headerHeight + this.getBoundingClientRect().height;
  }

  #getScrollRoot() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) {
      const overflowY = getComputedStyle(wrapper).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return wrapper;
    }

    return document.scrollingElement || document.documentElement;
  }

  #isDocumentRoot(root) {
    return root === document.scrollingElement || root === document.documentElement || root === document.body;
  }

  #targetFor(link) {
    return this.#targetForId(this.#normalizeId(link.getAttribute('href')?.replace('#', '')));
  }

  #targetForId(id) {
    if (!id) return null;
    return document.getElementById(id);
  }

  #normalizeId(value) {
    return (value || '').trim().toLowerCase();
  }
}

if (!customElements.get('darkrim-prod-nav')) {
  customElements.define('darkrim-prod-nav', DarkrimProdNav);
}
